import React from "react";
import { useTranslation } from "react-i18next";
import { LisLabels } from "../../screens/_label/lis_labels/lis_labels.js";
import * as menu from "../../assets/menus/menu.js";

/**
 * Reusable LIS main table with row click and pagination.
 *
 * Props:
 * - headers: Array<{ key: string, label?: string } | string>
 * - rows: Array<object>
 * - isLoading: boolean
 * - onRowClick?: (row) => void
 * - renderCell?: ({ row, header, value, rowIndex, colIndex }) => ReactNode
 * - renderActions?: (row) => ReactNode
 * - getRowKey?: (row, index) => string | number
 * - getRowStyle?: (row) => React.CSSProperties
 * - getRowTitle?: (row) => string
 * - noDataMessage?: string
 * - pagination?: { currentPage: number, totalPages: number, totalItems?: number, pageSize?: number }
 * - onPageChange?: (pageNumber: number) => void
 * - maxHeight?: number
 */
const LisMainTable = ({
  headers = [],
  rows = [],
  isLoading = false,
  onRowClick,
  renderCell,
  renderActions,
  getRowKey,
  getRowStyle,
  getRowTitle,
  noDataMessage,
  pagination,
  onPageChange,
  maxHeight = 450,
}) => {
  const { t } = useTranslation();
  const i18n = menu.getI18n();
  i18n.addResourceBundle("it", "lisLabels", LisLabels.it, true, true);
  i18n.addResourceBundle("en", "lisLabels", LisLabels.en, true, true);

  const resolvedNoDataMessage = noDataMessage ?? t("noData", { ns: "lisLabels" });
  const hasActions = typeof renderActions === "function";

  const computeRowKey = (row, idx) => {
    if (typeof getRowKey === "function") return getRowKey(row, idx);
    return row?.id ?? row?.Test_Id ?? idx;
  };

  const renderHeaderLabel = (h) => {
    return typeof h === "object" ? (h.label || h.key || "") : h;
  };

  const getHeaderKey = (h) => {
    return typeof h === "object" ? h.key : h;
  };

  const renderPageButtons = () => {
    if (!pagination || pagination.totalPages <= 1) return null;
    const { currentPage, totalPages } = pagination;
    const buttons = [];
    for (let i = 1; i <= totalPages; i += 1) {
      const isEdge = i === 1 || i === totalPages;
      const isNear =
        i >= currentPage - 2 && i <= currentPage + 2;
      if (isEdge || isNear) {
        buttons.push(
          <li
            key={i}
            className={`page-item ${currentPage === i ? "active" : ""}`}
          >
            <button
              className="page-link"
              onClick={() => onPageChange && onPageChange(i)}
            >
              {i}
            </button>
          </li>
        );
      } else if (i === currentPage - 3 || i === currentPage + 3) {
        buttons.push(
          <li key={`dots-${i}`} className="page-item disabled">
            <span className="page-link">...</span>
          </li>
        );
      }
    }
    return buttons;
  };

  return (
    <>
      {isLoading ? (
        <div
          className="table-responsive d-flex align-items-center justify-content-center"
          style={{
            maxHeight: `${maxHeight}px`,
            minHeight: `${maxHeight}px`,
            overflowX: "auto",
            overflowY: "auto",
          }}
        >
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Caricamento...</span>
          </div>
        </div>
      ) : rows.length === 0 ? (
        <div className="alert alert-info">
          <i className="fa-solid fa-info-circle me-2"></i>
          {resolvedNoDataMessage}
        </div>
      ) : (
        <>
          <div
            className="table-responsive"
            style={{
              maxHeight: `${maxHeight}px`,
              overflowX: "auto",
              overflowY: "auto",
            }}
          >
            <table className="table table-hover table-striped">
              <thead className="table-light sticky-top">
                <tr>
                  {headers.map((h, idx) => (
                    <th key={idx} style={{ whiteSpace: "nowrap" }}>
                      {renderHeaderLabel(h)}
                    </th>
                  ))}
                  {hasActions && (
                    <th style={{ whiteSpace: "nowrap" }}>Azioni</th>
                  )}
                </tr>
              </thead>
              <tbody>
                {rows.map((row, rowIdx) => {
                  const rowStyle =
                    typeof getRowStyle === "function"
                      ? getRowStyle(row)
                      : undefined;
                  const rowTitle =
                    typeof getRowTitle === "function"
                      ? getRowTitle(row)
                      : undefined;
                  return (
                    <tr
                      key={computeRowKey(row, rowIdx)}
                      onClick={() => onRowClick && onRowClick(row)}
                      style={rowStyle}
                      title={rowTitle}
                    >
                      {headers.map((h, colIdx) => {
                        const key = getHeaderKey(h);
                        const value = key != null ? row?.[key] : undefined;
                        const content =
                          typeof renderCell === "function"
                            ? renderCell({
                                row,
                                header: typeof h === "object" ? h : { key: key, label: String(h) },
                                value,
                                rowIndex: rowIdx,
                                colIndex: colIdx,
                              })
                            : value !== undefined && value !== null
                            ? String(value)
                            : "-";
                        return (
                          <td key={colIdx} style={{ whiteSpace: "nowrap" }}>
                            {content}
                          </td>
                        );
                      })}
                      {hasActions && (
                        <td
                          style={{ whiteSpace: "nowrap" }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          {renderActions(row)}
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {pagination && pagination.totalPages > 1 && (
            <div className="d-flex justify-content-between align-items-center mt-3">
              <div className="text-muted small">
                Pagina {pagination.currentPage} di {pagination.totalPages}
                {typeof pagination.totalItems === "number" ? (
                  <> ({pagination.totalItems} record totali)</>
                ) : null}
              </div>
              <nav>
                <ul className="pagination pagination-sm mb-0">
                  <li
                    className={`page-item ${
                      pagination.currentPage === 1 ? "disabled" : ""
                    }`}
                  >
                    <button
                      className="page-link"
                      onClick={() =>
                        onPageChange &&
                        onPageChange(Math.max(1, pagination.currentPage - 1))
                      }
                      disabled={pagination.currentPage === 1}
                    >
                      <i className="fa-solid fa-chevron-left"></i>
                    </button>
                  </li>
                  {renderPageButtons()}
                  <li
                    className={`page-item ${
                      pagination.currentPage === pagination.totalPages
                        ? "disabled"
                        : ""
                    }`}
                  >
                    <button
                      className="page-link"
                      onClick={() =>
                        onPageChange &&
                        onPageChange(
                          Math.min(
                            pagination.totalPages,
                            pagination.currentPage + 1
                          )
                        )
                      }
                      disabled={pagination.currentPage === pagination.totalPages}
                    >
                      <i className="fa-solid fa-chevron-right"></i>
                    </button>
                  </li>
                </ul>
              </nav>
            </div>
          )}
        </>
      )}
    </>
  );
};

export default LisMainTable;

